import type { ConditionGroup as ConditionGroupType, SingleCondition } from "@capture/shared/types";
import { isConditionGroup } from "@capture/shared/types";
import { ConditionRow, type ConditionLookupTable } from "./ConditionRow";
import type { FieldDefinitionSummary } from "../hooks/useJobTypeFields";

interface ConditionGroupProps {
  group: ConditionGroupType;
  fields: FieldDefinitionSummary[];
  onChange: (group: ConditionGroupType) => void;
  depth?: number;
  compact?: boolean;
  fieldNames?: string[];
  /** Tenant lookup tables — forwarded to ConditionRow for the lookup source. */
  lookupTables?: ConditionLookupTable[];
  /** P8 — job-level constant names, forwarded to ConditionRow's value picker. */
  constants?: string[];
}

function makeEmptyCondition(fields: FieldDefinitionSummary[]): SingleCondition {
  return {
    source: { type: "field", name: fields[0]?.name ?? "" },
    operator: "equals",
    value: { type: "literal", value: "" },
  };
}

export function ConditionGroupEditor({ group, fields, onChange, depth = 0, compact, fieldNames, lookupTables, constants }: ConditionGroupProps) {
  function toggleLogic() {
    onChange({ ...group, logic: group.logic === "and" ? "or" : "and" });
  }

  function updateCondition(index: number, updated: SingleCondition | ConditionGroupType) {
    const newConditions = [...group.conditions];
    newConditions[index] = updated;
    onChange({ ...group, conditions: newConditions });
  }

  function removeCondition(index: number) {
    const newConditions = group.conditions.filter((_, i) => i !== index);
    onChange({ ...group, conditions: newConditions });
  }

  function addCondition() {
    onChange({
      ...group,
      conditions: [...group.conditions, makeEmptyCondition(fields)],
    });
  }

  function addNestedGroup() {
    const nested: ConditionGroupType = {
      logic: group.logic === "and" ? "or" : "and",
      conditions: [makeEmptyCondition(fields)],
    };
    onChange({ ...group, conditions: [...group.conditions, nested] });
  }

  const borderColors = ["#ea580c", "#6d28d9", "#0e7490", "#b45309"];
  const borderColor = borderColors[depth % borderColors.length];
  const logicLabel = group.logic === "and" ? "AND" : "OR";
  const tooltip = group.logic === "and" ? "ALL must be true — click to switch to OR" : "ANY must be true — click to switch to AND";

  return (
    <div
      className={`cond-group cond-group--depth-${depth}${compact ? ' cond-group--compact' : ''}`}
      style={{ borderLeftColor: borderColor }}
      data-depth={depth}
    >
      <button
        type="button"
        className={`cond-group__chip cond-group__chip--${group.logic}`}
        style={{ background: borderColor }}
        onClick={toggleLogic}
        title={tooltip}
        aria-label={tooltip}
      >
        {logicLabel}
      </button>

      <div className="cond-group-body">
        {group.conditions.map((cond, i) => (
          <div key={i} className="cond-group-item">
            {i > 0 && (
              <div className="cond-logic-label">
                {group.logic.toUpperCase()}
              </div>
            )}
            {isConditionGroup(cond) ? (
              <ConditionGroupEditor
                group={cond}
                fields={fields}
                onChange={(updated) => updateCondition(i, updated)}
                depth={depth + 1}
                compact={compact}
                fieldNames={fieldNames}
                lookupTables={lookupTables}
                constants={constants}
              />
            ) : (
              <ConditionRow
                condition={cond}
                fields={fields}
                onChange={(updated) => updateCondition(i, updated)}
                onRemove={() => removeCondition(i)}
                lookupTables={lookupTables}
                constants={constants}
              />
            )}
          </div>
        ))}
      </div>

      <div className="cond-group-actions">
        <button className="cond-add-btn" onClick={addCondition}>
          + Add condition
        </button>
        {depth < 2 && (
          <button className="cond-add-btn cond-add-group" onClick={addNestedGroup}>
            + Add group ({group.logic === "and" ? "OR" : "AND"})
          </button>
        )}
      </div>
    </div>
  );
}
